import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Wand2, FileText, Sparkles, Settings, Palette, Hash, BookOpen, Shield } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
export interface VisualOptions {
  style: string;
  count: number | "auto";
  useFontAwesome: boolean;
  mobilePreview: boolean;
}
export interface WritingConstraints {
  preserveCitations: boolean;
  maintainFormalVoice: boolean;
  keepStandardCapitalization: boolean;
  avoidSlang: boolean;
  maintainStructure: boolean;
}
export interface WritingStyleConfig {
  style: string;
  constraints: WritingConstraints;
  customGuidelines?: string;
}
interface TextInputProps {
  onSubmit: (text: string, includeVisuals: boolean, visualOptions?: VisualOptions, intensity?: string, format?: string) => void;
  isLoading: boolean;
}
export const TextInput = ({
  onSubmit,
  isLoading
}: TextInputProps) => {
  const [text, setText] = useState("");
  const [includeVisuals, setIncludeVisuals] = useState(false);
  const [intensity, setIntensity] = useState("rewrite");
  const [submittedText, setSubmittedText] = useState(""); // Keep track of submitted text
  const [visualOptions, setVisualOptions] = useState<VisualOptions>({
    style: "auto",
    count: "auto",
    useFontAwesome: true,
    mobilePreview: false
  });
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Writing style configuration
  const [writingStyle, setWritingStyle] = useState("neutral");
  const [writingConstraints, setWritingConstraints] = useState<WritingConstraints>({
    preserveCitations: false,
    maintainFormalVoice: false,
    keepStandardCapitalization: false,
    avoidSlang: false,
    maintainStructure: false
  });
  const [customGuidelines, setCustomGuidelines] = useState("");
  const [showConstraints, setShowConstraints] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (text.trim()) {
      setSubmittedText(text.trim()); // Store the submitted text
      onSubmit(text.trim(), includeVisuals, includeVisuals ? visualOptions : undefined, intensity, writingStyle);
    }
  };
  const wordCount = (isLoading ? submittedText : text).trim().split(/\s+/).filter(word => word.length > 0).length;
  return <Card className="w-full bg-card border-border shadow-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <FileText className="w-5 h-5 text-primary" />
          Input Text
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="text">Paste your AI-generated text here</Label>
            <Textarea id="text" placeholder="Enter the text you want to humanize... The AI will make it sound more natural, conversational, and human-like while removing typical AI buzzwords." value={isLoading ? submittedText : text} onChange={e => setText(e.target.value)} className="min-h-[200px] bg-card border-border focus:ring-primary resize-none" disabled={isLoading} />
            <p className="text-sm text-muted-foreground">
              {wordCount} words
            </p>
          </div>
            <div className="space-y-4">
            {/* Writing Format Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Writing Format
              </Label>
              <RadioGroup value={writingStyle} onValueChange={value => {
              setWritingStyle(value);
              // Auto-enable constraints for academic style
              if (value === "academic") {
                setWritingConstraints({
                  preserveCitations: true,
                  maintainFormalVoice: true,
                  keepStandardCapitalization: true,
                  avoidSlang: true,
                  maintainStructure: true
                });
                setShowConstraints(true);
              } else if (value === "professional") {
                setWritingConstraints({
                  preserveCitations: false,
                  maintainFormalVoice: true,
                  keepStandardCapitalization: true,
                  avoidSlang: true,
                  maintainStructure: true
                });
              } else if (value === "neutral") {
                setWritingConstraints({
                  preserveCitations: false,
                  maintainFormalVoice: false,
                  keepStandardCapitalization: true,
                  avoidSlang: false,
                  maintainStructure: false
                });
              } else {
                // Reset constraints for conversational/plain styles
                setWritingConstraints({
                  preserveCitations: false,
                  maintainFormalVoice: false,
                  keepStandardCapitalization: false,
                  avoidSlang: false,
                  maintainStructure: false
                });
              }
            }} disabled={isLoading} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                <div className="flex items-center space-x-3 p-3 rounded-lg border border-border bg-card hover:bg-accent/5 transition-colors cursor-pointer">
                  <RadioGroupItem value="neutral" id="neutral" />
                  <Label htmlFor="neutral" className="flex-1 cursor-pointer font-normal">General Humanizer</Label>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg border border-border bg-card hover:bg-accent/5 transition-colors cursor-pointer">
                  <RadioGroupItem value="academic" id="academic" />
                  <Label htmlFor="academic" className="flex-1 cursor-pointer font-normal">Academic Humanizer</Label>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg border border-border bg-card hover:bg-accent/5 transition-colors cursor-pointer">
                  <RadioGroupItem value="professional" id="professional" />
                  <Label htmlFor="professional" className="flex-1 cursor-pointer font-normal">Business Humanizer</Label>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg border border-border bg-card hover:bg-accent/5 transition-colors cursor-pointer">
                  <RadioGroupItem value="conversational" id="conversational" />
                  <Label htmlFor="conversational" className="flex-1 cursor-pointer font-normal">Creative Humanizer</Label>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg border border-border bg-card hover:bg-accent/5 transition-colors cursor-pointer">
                  <RadioGroupItem value="plain" id="plain" />
                  <Label htmlFor="plain" className="flex-1 cursor-pointer font-normal">Technical Humanizer</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Writing Constraints */}
            

            {showConstraints && <div className="space-y-4 p-4 bg-muted/5 rounded-lg border border-border">
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex items-center space-x-2">
                    <Switch id="preserveCitations" checked={writingConstraints.preserveCitations} onCheckedChange={checked => setWritingConstraints(prev => ({
                  ...prev,
                  preserveCitations: checked
                }))} disabled={isLoading} />
                    <Label htmlFor="preserveCitations" className="text-sm">Preserve citations and references</Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch id="maintainFormalVoice" checked={writingConstraints.maintainFormalVoice} onCheckedChange={checked => setWritingConstraints(prev => ({
                  ...prev,
                  maintainFormalVoice: checked
                }))} disabled={isLoading} />
                    <Label htmlFor="maintainFormalVoice" className="text-sm">Maintain formal academic voice</Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch id="keepStandardCapitalization" checked={writingConstraints.keepStandardCapitalization} onCheckedChange={checked => setWritingConstraints(prev => ({
                  ...prev,
                  keepStandardCapitalization: checked
                }))} disabled={isLoading} />
                    <Label htmlFor="keepStandardCapitalization" className="text-sm">Keep standard capitalization</Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch id="avoidSlang" checked={writingConstraints.avoidSlang} onCheckedChange={checked => setWritingConstraints(prev => ({
                  ...prev,
                  avoidSlang: checked
                }))} disabled={isLoading} />
                    <Label htmlFor="avoidSlang" className="text-sm">Avoid slang and casual expressions</Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch id="maintainStructure" checked={writingConstraints.maintainStructure} onCheckedChange={checked => setWritingConstraints(prev => ({
                  ...prev,
                  maintainStructure: checked
                }))} disabled={isLoading} />
                    <Label htmlFor="maintainStructure" className="text-sm">Maintain paragraph and section structure</Label>
                  </div>
                </div>
                
                {writingStyle === "custom" && <div className="space-y-2 pt-3 border-t border-border">
                    <Label htmlFor="customGuidelines" className="text-sm font-medium">Custom Guidelines</Label>
                    <Textarea id="customGuidelines" placeholder="Enter specific instructions for how the text should be humanized..." value={customGuidelines} onChange={e => setCustomGuidelines(e.target.value)} className="min-h-[80px] bg-card border-border focus:ring-primary resize-none" disabled={isLoading} />
                  </div>}
              </div>}

            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Wand2 className="w-4 h-4" />
                Intensity Level
              </Label>
              <RadioGroup value={intensity} onValueChange={setIntensity} disabled={isLoading} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                <div className="flex items-center space-x-3 p-3 rounded-lg border border-primary bg-primary/5 hover:bg-primary/10 transition-colors cursor-pointer">
                  <RadioGroupItem value="rewrite" id="rewrite" />
                  <Label htmlFor="rewrite" className="flex-1 cursor-pointer font-normal">
                    <span className="font-medium">Rewrite 100%</span>
                    <span className="text-muted-foreground text-sm"> - Complete transformation </span>
                    <span className="text-primary text-sm font-medium">(Recommended)</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg border border-border bg-card hover:bg-accent/5 transition-colors cursor-pointer">
                  <RadioGroupItem value="light" id="light" />
                  <Label htmlFor="light" className="flex-1 cursor-pointer font-normal">
                    <span className="font-medium">Light</span>
                    <span className="text-muted-foreground text-sm"> - Minimal changes</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg border border-border bg-card hover:bg-accent/5 transition-colors cursor-pointer">
                  <RadioGroupItem value="medium" id="medium" />
                  <Label htmlFor="medium" className="flex-1 cursor-pointer font-normal">
                    <span className="font-medium">Medium</span>
                    <span className="text-muted-foreground text-sm"> - Natural improvements</span>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 transition-smooth shadow-glow" disabled={!text.trim() || isLoading}>
            {isLoading ? <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                Humanizing...
              </div> : <div className="flex items-center gap-2">
                <Wand2 className="w-4 h-4" />
                Humanize Text
              </div>}
          </Button>
        </form>
      </CardContent>
    </Card>;
};